/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { find } from 'lodash';

import { Checkbox } from '@motili/ui';
import { useUI } from 'common/context/UIContext';
import {
    ImagePreview,
    NotFoundPreview,
    OtherPreview,
    PDFPreview,
    PreviewContainer,
} from 'common/components/FilePreview';
import styled from 'styled-components';
import { generateRandomUUID } from 'common/utils/Utils';
import {
    isImageMimeType,
    isPdfMimeType,
} from 'common/components/file-upload/FileUploadTypes';
import { FormRow, FormElement } from '../form/FormLayout';
import FormTextView from '../form/FormTextView';
import FormTextArea from '../form/FormTextArea';
import FormSelect from '../form/FormSelect';
import MotiliButton from '../../components-v2/buttons/MotiliButton';
import Config from '../../../Config';
import styles from './DocumentRequirementsPhotos.module.scss';
import Drawer from '../../components-v2/modal/Drawer';
import MotiliButtonContainer from '../../components-v2/buttons/MotiliButtonContainer';
import {
    getVideoInfoFromUploadFile,
    isVideoMimeType,
} from './DocumentVideoUpload';
import { isVideo } from './DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';

export function DocumentRequirementsPhotosUploadDrawer(props) {
    const [busy, setBusy] = useState(false);
    const [uuid, setUuid] = useState();

    const title = useMemo(() => {
        if (props.imgSrc === Config.notApplicablePhoto.url) {
            return 'Upload Not Applicable Photo';
        }
        return 'Upload Photo';
    }, [props.imgSrc]);

    const photoTypes = useMemo(() => {
        return props.mimeType === 'application/pdf'
            ? props.photoTypes.filter(t => t.pdfSupported)
            : props.photoTypes;
    }, [props.mimeType, props.photoTypes]);

    useEffect(() => {
        if (props.open) {
            setUuid(generateRandomUUID());
        }
    }, [props.open]);

    return (
        <Drawer
            drawerStyle={{
                width: '45%',
                paddingBottom: '75px',
            }}
            title={
                <span
                    style={{ fontSize: 24, fontWeight: 600, color: '#333333' }}
                >
                    {title}
                </span>
            }
            onClose={props.handleCloseOnClick}
            closeByDocument={!busy}
            showClose={!busy}
            open={props.open}
        >
            <DocumentRequirementsPhotosContent
                key={uuid}
                currentPhotoTypeId={props.currentPhotoTypeId}
                currentRequiresPullAndClean={props.currentRequiresPullAndClean}
                photoTypes={photoTypes}
                photoTypeRequirements={props.photoTypeRequirements}
                photos={props.photos}
                fileName={props.fileName}
                imgSrc={props.imgSrc}
                mimeType={props.mimeType}
                action={props.action}
                handleCancelOnClick={props.handleCancelOnClick}
                handleUploadOnClick={props.handleUploadOnClick}
                busy={busy}
                setBusy={setBusy}
                file={props.file}
            />
        </Drawer>
    );
}
DocumentRequirementsPhotosUploadDrawer.propTypes = {
    handleCloseOnClick: PropTypes.func.isRequired,
    handleCancelOnClick: PropTypes.func.isRequired,
    handleUploadOnClick: PropTypes.func,
    photoTypeRequirements: PropTypes.arrayOf(PropTypes.object),
    photos: PropTypes.arrayOf(PropTypes.object),
    photoTypes: PropTypes.arrayOf(PropTypes.object),
    fileName: PropTypes.string.isRequired,
    imgSrc: PropTypes.string,
    mimeType: PropTypes.string.isRequired,
    currentPhotoTypeId: PropTypes.string,
    currentRequiresPullAndClean: PropTypes.bool,
    action: PropTypes.shape({ display: PropTypes.string }),
    open: PropTypes.bool,
    file: PropTypes.any,
};

const DocumentRequirementsPhotosContent = props => {
    const [typeId, setTypeId] = useState();
    const [description, setDescription] = useState(
        props.imgSrc === Config.notApplicablePhoto.url
            ? Config.notApplicablePhoto.defaultDescription
            : ''
    );
    const [requiresPullAndClean, setRequiresPullAndClean] = useState(
        props.currentRequiresPullAndClean || false
    );
    const [videoThumbnail, setVideoThumbnail] = useState();

    useEffect(() => {
        if (isVideoMimeType(props.mimeType) && props.file) {
            getVideoInfoFromUploadFile(props.file)
                .then(({ thumbnailUrl }) => {
                    setVideoThumbnail(thumbnailUrl);
                })
                .catch(console.error);
        }
    }, [props.mimeType, props.file]);

    const handleUploadOnClick = () => {
        props.setBusy(true);
        props
            .handleUploadOnClick({
                typeId,
                description,
                requiresPullAndClean,
            })
            .finally(() => props.setBusy(false));
    };

    const photoTypes = useMemo(() => {
        const applyLogicDeprecatedDamagedRepairedArea = photoTypes => {
            let _photoTypes = [...photoTypes];
            const damageAreaInClientRequirement =
                props.photoTypeRequirements.some(
                    photo => photo.requirement.id === 'DAMAGED_AREA'
                );
            const repairedAreaInClientRequirement =
                props.photoTypeRequirements.some(
                    photo => photo.requirement.id === 'REPAIRED_AREA'
                );
            const damageAreaInWORequirement = props.photos.some(
                photo => photo.requirement.photoTypeId === 'DAMAGED_AREA'
            );
            const repairedAreaInWORequirement = props.photos.some(
                photo => photo.requirement.photoTypeId === 'REPAIRED_AREA'
            );
            if (damageAreaInClientRequirement || damageAreaInWORequirement) {
                _photoTypes = _photoTypes.filter(
                    type => type.id !== 'AREA_PART_NEEDING_REPAIR'
                );
            } else {
                _photoTypes = _photoTypes.filter(
                    type => type.id !== 'DAMAGED_AREA'
                );
            }
            if (
                repairedAreaInClientRequirement ||
                repairedAreaInWORequirement
            ) {
                _photoTypes = _photoTypes.filter(
                    type => type.id !== 'AREA_PART_REPAIRED'
                );
            } else {
                _photoTypes = _photoTypes.filter(
                    type => type.id !== 'REPAIRED_AREA'
                );
            }
            return _photoTypes;
        };

        const mapPhotoTypesWithRequirements = () => {
            const requiredPhotos = [];
            const metPhotos = [];
            const otherPhotos = [];
            props.photoTypes.forEach(photoType => {
                const matchedPhotoTypeRequirement = find(
                    props.photoTypeRequirements,
                    {
                        requirement: { id: photoType.id },
                    }
                );
                const photos = props.photos?.filter(photo => !isVideo(photo));
                if (matchedPhotoTypeRequirement) {
                    if (
                        find(photos, {
                            requirement: {
                                photoTypeId:
                                    matchedPhotoTypeRequirement.requirement.id,
                            },
                        }) ||
                        find(photos, {
                            metProjectRequirementId:
                                matchedPhotoTypeRequirement.id,
                        })
                    ) {
                        return metPhotos.push({
                            ...photoType,
                            display: `${photoType.display} (Met)`,
                        });
                    }
                    return requiredPhotos.push({
                        ...photoType,
                        display: `${photoType.display} (Required)`,
                    });
                }

                return otherPhotos.push(photoType);
            });

            return requiredPhotos.concat(metPhotos).concat(otherPhotos);
        };
        let photoTypes = mapPhotoTypesWithRequirements().filter(
            photoType =>
                props.photoTypeRequirements.some(
                    p => p.requirement.id === photoType.id
                ) ||
                props.photos.some(
                    p =>
                        p.requirement.photoTypeId === photoType.id &&
                        photoType.id !== 'LIFE_HEALTH_SAFETY_RISK'
                ) ||
                (!photoType.deprecated &&
                    photoType.id !== 'LIFE_HEALTH_SAFETY_RISK')
        );
        // CS-2624
        photoTypes = applyLogicDeprecatedDamagedRepairedArea(photoTypes);
        return photoTypes;
    }, [props.photoTypeRequirements, props.photoTypes, props.photos]);

    const isPullAndCleanBeforePhoto = useMemo(() => {
        return (
            ['BLOWER_WHEEL_BEFORE', 'INDOOR_COIL_BEFORE'].includes(
                props.currentPhotoTypeId || typeId
            ) && !props.mimeType?.startsWith('video/')
        );
    }, [props.currentPhotoTypeId, typeId, props.mimeType]);

    const { pullAndCleanWaring, pullAndCleanInstruction } = useMemo(() => {
        return {
            pullAndCleanWaring:
                'By selecting a Pull and Clean for this requirement, an automatic N/A photo will be applied to the After photo.',
            pullAndCleanInstruction:
                'You must tag/identify this piece of equipment and select ‘Repair’ within the Equipment section of this Work Order before Submitting.',
        };
    }, []);

    const imagePreview = useMemo(() => {
        if (!props.imgSrc) {
            return <NotFoundPreview />;
        }
        if (isImageMimeType(props.mimeType)) {
            return (
                <StyledImagePreview
                    src={props.imgSrc}
                    alt={props.fileName || 'preview'}
                />
            );
        }
        if (isPdfMimeType(props.mimeType)) {
            return <PDFPreview />;
        }
        if (isVideoMimeType(props.mimeType)) {
            if (videoThumbnail) {
                return (
                    <StyledImagePreview
                        src={videoThumbnail}
                        alt={props.fileName || 'preview'}
                    />
                );
            }
            return <OtherPreview />;
        }
        return <OtherPreview />;
    }, [props.fileName, props.imgSrc, props.mimeType, videoThumbnail]);

    const { transformLabel } = useUI();

    return (
        <>
            <FormRow>
                <FormElement>
                    <StyledPreviewContainer>
                        {imagePreview}
                    </StyledPreviewContainer>
                </FormElement>
                <FormElement>
                    <FormRow>
                        <FormElement className={styles.photoFileName}>
                            <FormTextView
                                bypassPreTag
                                label={transformLabel('Name')}
                                value={props.fileName}
                            />
                        </FormElement>
                    </FormRow>
                    <FormRow>
                        <FormElement>
                            <FormSelect
                                disabled={!!props.currentPhotoTypeId}
                                label={transformLabel('Type')}
                                options={photoTypes}
                                value={props.currentPhotoTypeId || typeId}
                                onChange={opt => {
                                    setTypeId(opt.id);
                                    setRequiresPullAndClean(false);
                                }}
                                required
                            />
                        </FormElement>
                    </FormRow>
                    <FormRow>
                        <FormElement>
                            <FormTextArea
                                label={transformLabel('Description')}
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                inputStyle={{
                                    minHeight: 80,
                                }}
                            />
                        </FormElement>
                    </FormRow>
                    {isPullAndCleanBeforePhoto && (
                        <FormRow>
                            <FormElement>
                                <Checkbox
                                    label='Requires Pull and Clean'
                                    id='document-upload-modal-pull-and-clean-option'
                                    value=''
                                    name='document-upload-modal-pull-and-clean-option'
                                    onChange={e =>
                                        setRequiresPullAndClean(
                                            e.target.checked
                                        )
                                    }
                                    checked={requiresPullAndClean}
                                    color='gray'
                                />
                            </FormElement>
                        </FormRow>
                    )}
                    {isPullAndCleanBeforePhoto && requiresPullAndClean && (
                        <>
                            <FormRow>
                                <FormElement
                                    className={styles.pullCleanWarning}
                                >
                                    <div>{pullAndCleanWaring}</div>
                                </FormElement>
                            </FormRow>
                            <FormRow>
                                <FormElement
                                    className={styles.pullCleanWarning}
                                >
                                    <div>{pullAndCleanInstruction}</div>
                                </FormElement>
                            </FormRow>
                        </>
                    )}
                </FormElement>
            </FormRow>

            <MotiliButtonContainer
                styles={{
                    marginTop: '40px',
                }}
            >
                <MotiliButton
                    text='Cancel'
                    moStyle='normal'
                    onClick={props.handleCancelOnClick}
                    disabled={props.busy}
                    style={{ width: '120px' }}
                />
                <MotiliButton
                    text='Upload'
                    onClick={() => handleUploadOnClick()}
                    busy={props.busy}
                    disabled={
                        (!typeId && !props.currentPhotoTypeId) ||
                        (props.currentPhotoTypeId &&
                            !photoTypes.some(type => {
                                return type.id === props.currentPhotoTypeId;
                            })) ||
                        props.busy
                    }
                    style={{ width: '120px' }}
                />
            </MotiliButtonContainer>
        </>
    );
};
DocumentRequirementsPhotosContent.propTypes = {
    photoTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
    fileName: PropTypes.string.isRequired,
    imgSrc: PropTypes.string.isRequired,
    mimeType: PropTypes.string.isRequired,
    currentRequiresPullAndClean: PropTypes.bool,
    currentPhotoTypeId: PropTypes.string,
    photoTypeRequirements: PropTypes.arrayOf(PropTypes.object),
    photos: PropTypes.arrayOf(PropTypes.object),
    busy: PropTypes.bool,
    setBusy: PropTypes.func,
    handleCancelOnClick: PropTypes.func,
    handleUploadOnClick: PropTypes.func,
    file: PropTypes.any,
};

const StyledImagePreview = styled(ImagePreview)`
    max-width: 100%;
    width: auto;
`;
const StyledPreviewContainer = styled(PreviewContainer)`
    height: 100%;
    align-items: flex-start;
`;
